import { useCombatStore } from '@/state/useCombatStore'
import { useEconomyStore } from '@/state/useEconomyStore'
import { WEAPONS, WEAPON_ORDER } from '@/combat/weaponTypes'
import type { WeaponClass } from '@/combat/weaponTypes'
import { formatFollis } from '@/economy/types'
import type { WalletInfo } from '@/economy/types'
import type { InteractionTarget } from '@/state/useEconomyStore'

interface PanelProps { interaction: InteractionTarget; wallet: WalletInfo | null }

const CATEGORY_LABEL = { melee: 'Melee', ranged: 'Ranged', thrown: 'Thrown' }

export function ArmoryWeaponsPanel({ wallet }: PanelProps) {
  const unlockedWeapons = useCombatStore((s) => s.unlockedWeapons)
  const equippedWeapon = useCombatStore((s) => s.equippedWeapon)
  const unlockWeapon = useCombatStore((s) => s.unlockWeapon)
  const equipWeapon = useCombatStore((s) => s.equipWeapon)
  const setWallet = useEconomyStore((s) => s.setWallet)

  function handleBuy(weapon: WeaponClass) {
    const info = WEAPONS[weapon]
    if (!wallet || wallet.follis < info.price) return
    // Deduct locally (server-authoritative would go through API)
    unlockWeapon(weapon)
    setWallet({
      ...wallet,
      follis: wallet.follis - info.price,
      total_follis_value: wallet.total_follis_value - info.price,
    })
  }

  function handleEquip(weapon: WeaponClass) {
    equipWeapon(weapon)
  }

  return (
    <>
      <div className="panel-section">
        <h3 className="panel-section__title">Weapons for Sale</h3>
        <p style={{ color: '#8b7355', fontFamily: 'Crimson Text, serif', fontSize: 12, marginBottom: 8 }}>
          Browse the armory's finest weapons. Press 1-5 to switch in combat.
        </p>

        {WEAPON_ORDER.map((w) => {
          const info = WEAPONS[w]
          const owned = unlockedWeapons.includes(w)
          const equipped = equippedWeapon === w
          const canAfford = wallet && wallet.follis >= info.price

          return (
            <div
              key={w}
              className="panel-section"
              style={{
                background: equipped ? 'rgba(60, 90, 70, 0.2)' : 'rgba(30, 28, 24, 0.5)',
                padding: 10,
                borderRadius: 4,
                marginBottom: 8,
                border: equipped ? '1px solid #3a6a4a' : '1px solid transparent',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontFamily: 'Cinzel, serif', fontSize: 14, fontWeight: 700, color: '#d4e0da' }}>
                  {info.name}
                </span>
                <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#6a8a7a' }}>
                  {CATEGORY_LABEL[info.category]}
                </span>
              </div>

              <p style={{ color: '#8b9a85', fontFamily: 'Crimson Text, serif', fontSize: 12, margin: '4px 0 6px' }}>
                {info.description}
              </p>

              <div style={{ display: 'flex', gap: 14, fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#6a8a7a', marginBottom: 6 }}>
                <span>DMG <span style={{ color: '#8ab8a0' }}>{info.damage}</span></span>
                <span>SPD <span style={{ color: '#8ab8a0' }}>{info.attackSpeed.toFixed(1)}</span></span>
                <span>RNG <span style={{ color: '#8ab8a0' }}>{info.range}</span></span>
              </div>

              <p style={{ color: '#6a7a70', fontFamily: 'Crimson Text, serif', fontSize: 11, margin: '2px 0 6px' }}>
                Ability: {info.abilityDescription}
              </p>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {owned ? (
                  equipped ? (
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#5a9e80' }}>Equipped</span>
                  ) : (
                    <button className="panel-btn panel-btn--primary" onClick={() => handleEquip(w)}>
                      Equip
                    </button>
                  )
                ) : info.price === 0 ? (
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#5a9e80' }}>Free — always available</span>
                ) : (
                  <button
                    className="panel-btn panel-btn--primary"
                    disabled={!canAfford}
                    onClick={() => handleBuy(w)}
                  >
                    Buy — {formatFollis(info.price)}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <div className="panel-section">
        <h3 className="panel-section__title">Controls</h3>
        <div className="panel-row">
          <span className="panel-row__label">Left Click</span>
          <span className="panel-row__value">Attack</span>
        </div>
        <div className="panel-row">
          <span className="panel-row__label">Right Click</span>
          <span className="panel-row__value">Block (Harpoon)</span>
        </div>
        <div className="panel-row">
          <span className="panel-row__label">R</span>
          <span className="panel-row__value">Ability</span>
        </div>
        <div className="panel-row">
          <span className="panel-row__label">1-5</span>
          <span className="panel-row__value">Switch Weapon</span>
        </div>
      </div>
    </>
  )
}
